<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, type Ref } from "vue";
import { isUserPermitted } from "~/utils/permissionHelper";
import { useGlobalComponentStore } from "~/stores/globalComponentStore";
const route = useRoute();
const globalComponentStore = useGlobalComponentStore();

const closeShowMore = () => {
  if (globalComponentStore._showMoreModal) {
    globalComponentStore.setShowMoreModalVisibility(false);
  }
};

const props = defineProps({
  sideNavData: { required: true, type: Object },
});

// Supplied by SideNav while the rail is hover-expanded; defaults keep this
// component usable on its own.
const isRailExpanded = inject<Ref<boolean>>("sideNavExpanded", ref(false));
// Shared across every group in one rail so only one flyout is ever open: a
// diagonal pointer path that grazes a neighbouring row can otherwise leave two
// lists on screen at once while the first one waits out its close delay.
const openFlyout = inject<Ref<string | null>>(
  "sideNavOpenFlyout",
  ref<string | null>(null),
);

const rowEl = ref<HTMLElement | null>(null);
const flyoutKey = computed(() => props.sideNavData.parentName as string);
const isOpen = computed(() => openFlyout.value === flyoutKey.value);

let openTimer: ReturnType<typeof setTimeout> | null = null;
let closeTimer: ReturnType<typeof setTimeout> | null = null;

const clearTimers = () => {
  if (openTimer) {
    clearTimeout(openTimer);
    openTimer = null;
  }
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
};

// A short open delay means a pointer merely passing over this row on its way to
// another group's flyout never steals it; the longer close delay forgives the
// diagonal path back out towards the children.
const scheduleOpen = () => {
  clearTimers();
  if (isOpen.value) return;
  openTimer = setTimeout(() => {
    openFlyout.value = flyoutKey.value;
  }, 90);
};

const scheduleClose = () => {
  clearTimers();
  closeTimer = setTimeout(() => {
    if (isOpen.value) openFlyout.value = null;
  }, 220);
};

const openNow = () => {
  clearTimers();
  openFlyout.value = flyoutKey.value;
};

const closeNow = () => {
  clearTimers();
  if (isOpen.value) openFlyout.value = null;
};

// Keyboard users land on the row itself (it is focusable) and then tab straight
// into the children, which live inside this row in the DOM even though the
// flyout paints outside it — so `contains` still answers correctly here.
const handleFocusOut = (event: FocusEvent) => {
  const next = event.relatedTarget as Node | null;
  if (next && rowEl.value?.contains(next)) return;
  scheduleClose();
};

const handleChildClick = () => {
  closeNow();
  closeShowMore();
};

onBeforeUnmount(clearTimers);

// create a computed property which will check if the route.path matches with any of the child paths if so return the string 'router-link-active' or else ''
const isActive = computed(() => {
  return props.sideNavData.children.some((child) => child.path === route.path)
    ? "router-link-active"
    : "";
});
</script>
<template>
  <!-- Desktop rail. The row spans the full panel width in both states so the
       flyout can anchor to the panel's edge instead of a fixed pixel offset. -->
  <div
    ref="rowEl"
    class="nav-row hidden sm:flex"
    :class="{ 'is-expanded': isRailExpanded }"
    @mouseenter="scheduleOpen"
    @mouseleave="scheduleClose"
    @focusin="openNow"
    @focusout="handleFocusOut"
  >
    <div
      class="side-icon nav-entry"
      :class="isActive"
      tabindex="0"
      role="button"
      :aria-expanded="isOpen"
      :aria-label="props.sideNavData.parentName"
      @keydown.enter.prevent="openNow"
      @keydown.space.prevent="openNow"
    >
      <img
        :src="`/svgs/sidebar/${props.sideNavData.imgName}.svg`"
        class="side-icon-img shrink-0"
      />
      <span class="nav-label">{{ props.sideNavData.parentName }}</span>
      <svg
        class="nav-chevron"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m9 6 6 6-6 6" />
      </svg>
    </div>
    <SideNavFlyout :open="isOpen" :anchor="rowEl">
      <div class="flyout-title">{{ props.sideNavData.parentName }}</div>
      <div class="flyout-list flex flex-col">
        <NuxtLink
          v-for="child in sideNavData.children"
          v-show="isUserPermitted(child.permissionName)"
          :key="child.path"
          :to="child.path"
          class="flyout-link"
          @click="handleChildClick"
        >
          {{ child.name }}
        </NuxtLink>
      </div>
    </SideNavFlyout>
  </div>
  <!-- Mobile "More" sheet: children listed inline, unchanged. -->
  <div class="flex flex-col gap-2 p-2 sm:hidden">
    <div class="flex gap-2 items-center">
      <img
        :src="`/svgs/sidebar/${props.sideNavData.imgName}.svg`"
        class="side-icon-img"
      />
      <div class="text-white">
        {{ props.sideNavData.parentName }}
      </div>
    </div>
    <div class="flex flex-col pl-5">
      <div
        v-for="child in sideNavData.children"
        v-show="isUserPermitted(child.permissionName)"
        :key="child"
        class="hover:bg-white/[.10] rounded-md"
      >
        <NuxtLink :to="child.path" class="px-2" @click="closeShowMore">
          <span class="text-white"> |- {{ child.name }} </span>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
<style scoped>
.router-link-active {
  background-color: rgb(255 255 255 / 0.2);
  border-color: rgb(255 255 255 / 0.5);
  box-shadow: 0 12px 22px rgba(0, 0, 0, 0.25);
}
.scrollbar-thin {
  scrollbar-width: thin;
}

/* Wide enough that a two-word child never wraps, narrow enough that a flyout
   off a short group does not look like a second panel. */
.flyout-list {
  min-width: 10rem;
}

.flyout-title {
  padding: 0.125rem 0.625rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(255 255 255 / 0.55);
}

.flyout-link {
  display: block;
  border-radius: 0.5rem;
  padding: 0.5rem 0.625rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: rgb(255 255 255 / 0.85);
  transition:
    background-color 120ms ease,
    color 120ms ease;
}

.flyout-link:hover {
  background-color: rgb(255 255 255 / 0.12);
  color: #fff;
}

/* Children carry the route-active highlight too — flat, not the raised look the
   rail button gets, so the open group and the current page stay distinguishable. */
.flyout-link.router-link-active {
  background-color: rgb(255 255 255 / 0.16);
  box-shadow: none;
  color: #fff;
}

@media (min-width: 640px) {
  .nav-row {
    position: relative;
    justify-content: center;
  }

  .nav-row.is-expanded {
    justify-content: flex-start;
  }

  /* Collapsed: a centred icon square, as before. Expanded: the button fills the
     row and the label slides out. Width/opacity only — a transform here would
     make the panel a containing block for the fixed flyouts. */
  .nav-entry {
    flex: 0 0 auto;
    align-items: center;
    overflow: hidden;
    cursor: pointer;
    transition:
      flex-grow var(--rail-motion, 200ms) var(--rail-ease, ease),
      width var(--rail-motion, 200ms) var(--rail-ease, ease),
      background-color 150ms ease;
  }

  .nav-entry:focus-visible {
    outline: 2px solid rgb(255 255 255 / 0.6);
    outline-offset: 1px;
  }

  .nav-row.is-expanded .nav-entry {
    flex: 1 1 auto;
    gap: 0.625rem;
  }

  .nav-label {
    max-width: 0;
    opacity: 0;
    white-space: nowrap;
    overflow: hidden;
    color: #fff;
    font-size: 0.875rem;
    line-height: 1.25rem;
    transition:
      max-width var(--rail-motion, 200ms) var(--rail-ease, ease),
      opacity var(--rail-motion, 200ms) var(--rail-ease, ease);
  }

  .nav-row.is-expanded .nav-label {
    max-width: 9rem;
    opacity: 1;
  }

  /* The chevron is the affordance that says "this one has children"; it only
     makes sense once there is a label for it to sit after. */
  .nav-chevron {
    width: 0;
    height: 1rem;
    margin-left: auto;
    opacity: 0;
    color: rgb(255 255 255 / 0.6);
    transition:
      width var(--rail-motion, 200ms) var(--rail-ease, ease),
      opacity var(--rail-motion, 200ms) var(--rail-ease, ease);
  }

  .nav-row.is-expanded .nav-chevron {
    width: 1rem;
    opacity: 1;
  }
}
</style>
